import { Component, computed, input } from '@angular/core';

import { DslError, Token, tokenize } from '@core/filters/path-filter/dsl/lexer';
import { OPERATOR_START_WORDS } from '@core/filters/path-filter/dsl/parser';
import { RELATIVE_DATE_DSL } from '@core/filters/path-filter/dsl/printer';

export type DslRole = 'keyword' | 'field' | 'edge' | 'literal' | 'space';

export interface DslSegment {
  text: string;
  role: DslRole;
}

const KEYWORDS: ReadonlySet<string> = new Set([
  ...OPERATOR_START_WORDS,
  'and',
  'or',
  'not',
  'all',
  'any',
  'is',
  'in',
  'empty',
  'of',
  'tag',
  'with',
  'no',
  'none',
  'within',
  'between',
  'contains',
  'starts',
  'ends',
  'matches',
  'references',
  'has',
  'label',
  'self',
  'uid',
  'uids',
  'penalty',
  'when',
  'context',
  'position',
  'service_window'
]);

const LITERAL_WORDS: ReadonlySet<string> = new Set([
  'true',
  'false',
  'null',
  ...Object.values(RELATIVE_DATE_DSL)
]);

const LITERAL_TOKENS: ReadonlySet<Token['kind']> = new Set([
  'str',
  'int',
  'num',
  'quantity',
  'duration',
  'date',
  'date_time',
  'time'
]);

const ROLE_CLASSES: Record<DslRole, string> = {
  keyword: 'text-text-tertiary',
  field: 'text-text-primary',
  edge: 'font-semibold text-text-primary',
  literal: 'text-accent-main',
  space: ''
};

function roleOf(tokens: Token[], index: number): DslRole {
  const token = tokens[index];
  if (LITERAL_TOKENS.has(token.kind)) return 'literal';
  if (token.kind === 'name') return 'field';
  if (token.kind !== 'word') return 'keyword';
  if (LITERAL_WORDS.has(token.value)) return 'literal';
  const [open, quantifier, close, paren] = tokens.slice(index + 1, index + 5);
  if (
    open?.kind === '[' &&
    (quantifier?.value === 'any' || quantifier?.value === 'all') &&
    close?.kind === ']' &&
    paren?.kind === '('
  ) {
    return 'edge';
  }
  return KEYWORDS.has(token.value) ? 'keyword' : 'field';
}

/** `null` when the text does not lex, so the caller shows it unstyled. */
export function dslSegments(text: string): DslSegment[] | null {
  let tokens: Token[];
  try {
    tokens = tokenize(text);
  } catch (error) {
    if (error instanceof DslError) return null;
    throw error;
  }
  const segments: DslSegment[] = [];
  let at = 0;
  tokens.forEach((token, index) => {
    if (token.start > at)
      segments.push({ text: text.slice(at, token.start), role: 'space' });
    segments.push({
      text: text.slice(token.start, token.end),
      role: roleOf(tokens, index)
    });
    at = token.end;
  });
  if (at < text.length) segments.push({ text: text.slice(at), role: 'space' });
  return segments;
}

/** Printed DSL, read-only, coloured by token role. */
@Component({
  selector: 'app-dsl-text',
  templateUrl: './dsl-text.component.html',
  styles: `
    :host {
      display: inline;
      min-width: 0;
    }
  `
})
export class DslTextComponent {
  text = input.required<string>();

  protected readonly segments = computed(() => dslSegments(this.text()));
  protected readonly ROLE_CLASSES = ROLE_CLASSES;
}
